import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, take, timeout, catchError, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { AppState } from '../../state/app.state';
import { selectLoginSuccess } from '../../state/auth/auth.selectors';

/**
 * Helper function to check if setup is complete
 * Handles various value types: boolean, number (0/1), string ("0"/"1"), null, undefined
 */
function isSetupComplete(value: any): boolean {
  // Explicit false, 0, "0", "", null, undefined means NOT complete
  if (value === true || value === 1 || value === '1') {
    return true;
  }
  return false;
}

/**
 * OnboardingGuard - Redirects users who haven't completed setup to the onboarding flow
 *
 * This guard checks if the user has completed their onboarding setup.
 * If not, they are redirected to /onboarding.
 * If yes, they can proceed to the requested route.
 */
export const onboardingGuard: CanActivateFn = (route, state) => {
  const store = inject(Store<AppState>);
  const router = inject(Router);

  return store.select(selectLoginSuccess).pipe(
    filter(data => data.isLoggedIn === true),
    take(1),
    timeout(3000),
    map(data => {
      const user = data.user;

      // Check if user has completed setup (handle various value types)
      const setupComplete = isSetupComplete(user?.has_completed_setup);

      if (user && !setupComplete) {
        router.navigate(['/onboarding']);
        return false;
      }

      return true;
    }),
    catchError((error) => {
      console.error('[OnboardingGuard] Error or timeout:', error);
      // If we can't determine the user's status, redirect to login
      router.navigate(['/login']);
      return of(false);
    })
  );
};

/**
 * CompletedOnboardingGuard - Redirects users who HAVE completed setup away from /onboarding
 *
 * This guard is used on the /onboarding route to prevent users who have already
 * completed their setup from accessing the onboarding flow again.
 * If setup is complete, they are redirected to /dashboard.
 */
export const completedOnboardingGuard: CanActivateFn = (route, state) => {
  const store = inject(Store<AppState>);
  const router = inject(Router);

  return store.select(selectLoginSuccess).pipe(
    filter(data => data.isLoggedIn === true),
    take(1),
    timeout(3000),
    map(data => {
      const user = data.user;

      // Check if user has completed setup
      const setupComplete = isSetupComplete(user?.has_completed_setup);

      if (user && setupComplete) {
        router.navigate(['/dashboard']);
        return false;
      }

      return true;
    }),
    catchError((error) => {
      console.error('[CompletedOnboardingGuard] Error or timeout:', error);
      // If we can't determine the user's status, allow access to onboarding
      // (they'll be redirected to login by AuthGuard if not logged in)
      return of(true);
    })
  );
};
